import { itemModalTrackingEvents } from 'integrations/segment/tracking-events';
import { trackAddRecommendedItemToCart } from 'integrations/segment/tracking-events/itemModal';
import { cartFormatting } from 'integrations/segment/util/formatting';
import Cart from 'models/Cart';
import CartItem from 'models/CartItem';
import CartModifier from 'models/CartModifier';
import MenuItem from 'models/MenuItem';
import SharedCartItem from 'models/SharedCartItem';
import MenuDataStore from 'stores/MenuDataStore';
import RootStore from 'stores/RootStore';
import combineURLs from 'axios/lib/helpers/combineURLs';
import { LoyaltyReward } from 'api/types';

const { trackClickRemoveCartItem, trackClickQuantityStepper } = itemModalTrackingEvents;
const { formatCartItemForTracking } = cartFormatting;

export const buildBreadcrumbs = (displayedItem: CartItem | CartModifier) => {
  const itemList = [];
  let current = displayedItem;

  while (current) {
    itemList.push(current);
    current = current.parent;
  }

  return itemList.reverse();
};

export const addItemToCart = (
  itemId: string,
  selectedCart: Cart,
  newCartItem: CartItem | SharedCartItem | undefined,
  referringItem?: MenuItem
) => {
  trackAddRecommendedItemToCart({
    itemId,
    referringItemName: referringItem?.name_for_customer,
    referringItemId: referringItem?.id,
  });
  if (newCartItem) {
    selectedCart.handleAddItemToCart(newCartItem);
  }
};

export const removeItemFromCart = (selectedCart: Cart, cartItem?: CartItem | SharedCartItem) => {
  cartItem && selectedCart.removeItem(cartItem, cartItem.hash());
  trackClickRemoveCartItem(formatCartItemForTracking(cartItem));
};

export const handleUpdateQty = (
  setAppliedLoyaltyRewards: (loyaltyReward: LoyaltyReward | undefined) => void,
  cartItem: CartItem | SharedCartItem,
  newQuantity: number,
  quantityInCart: number
) => {
  setAppliedLoyaltyRewards(undefined);
  cartItem.setQty(newQuantity);
  trackClickQuantityStepper({
    type: newQuantity > quantityInCart ? 'increment' : 'decrement',
    ...formatCartItemForTracking(cartItem),
  });
};

export const handleDecreaseQty = (params: {
  setAppliedLoyaltyRewards: (loyaltyReward: LoyaltyReward | undefined) => void;
  cartItem?: CartItem | SharedCartItem;
}) => {
  params.setAppliedLoyaltyRewards(undefined);
  params?.cartItem?.setQty(params?.cartItem.qty - 1);
  trackClickQuantityStepper({
    type: 'decrement',
    ...formatCartItemForTracking(params?.cartItem),
  });
};

export const handleIncreaseQty = (params: {
  setAppliedLoyaltyRewards: (loyaltyReward: LoyaltyReward | undefined) => void;
  cartItem?: CartItem | SharedCartItem;
}) => {
  params.setAppliedLoyaltyRewards(undefined);
  params?.cartItem?.setQty(params?.cartItem.qty + 1);
  trackClickQuantityStepper({
    type: 'increment',
    ...formatCartItemForTracking(params?.cartItem),
  });
};

export const getConstsForRecommendation = (
  selectedCart: Cart,
  useItemSheet: any,
  history: any,
  getLinkToAddItem: any,
  imageUrl: string,
  rootStore: RootStore,
  anonymousUserId: string,
  menuId: string,
  itemId: string,
  mostLoved: boolean,
  menuDataStore: MenuDataStore
) => {
  const { items, isSharedCart, sharedCartReference } = selectedCart;
  const itemInCart = items.some((item) => item.menu_item_id === itemId);
  const linkWithoutParam = getLinkToAddItem({ itemId, menuId }) as string;
  const linkToItem = mostLoved ? `${linkWithoutParam}&fromCarousel=true` : getLinkToAddItem({ itemId, menuId });
  const pushCartItem = useItemSheet()?.pushCartItem ?? (() => history.push(linkToItem));
  const imgSrc = imageUrl?.includes('http') ? imageUrl : combineURLs(process.env.REACT_APP_STATIC_ROOT ?? '', imageUrl);
  const cartItemParams = {
    menu_id: menuId ?? '',
    menu_item_id: itemId,
    special_instructions: '',
  };
  const newCartItem =
    isSharedCart && sharedCartReference
      ? new SharedCartItem(rootStore, sharedCartReference, {
          owner_id: anonymousUserId,
          ...cartItemParams,
        })
      : new CartItem(menuDataStore, cartItemParams);

  return {
    itemInCart,
    pushCartItem,
    imgSrc,
    newCartItem,
  };
};

export const getConstsForOneClickAdd = ({
  selectedCart,
  rootStore,
  anonymousUserId,
  menuId,
  itemId,
  menuDataStore,
}: {
  selectedCart: Cart;
  rootStore: RootStore;
  anonymousUserId: string;
  menuId: string;
  itemId: string;
  menuDataStore: MenuDataStore;
}) => {
  // Get cart items
  const { isSharedCart, sharedCartReference } = selectedCart;
  const sharedCartItems = sharedCartReference?.sharedCartItems.filter((item) => item.owner_id === anonymousUserId);
  const regularCartItems = selectedCart.getItems();

  // Get quantity in cart
  const sharedCartItemQty = sharedCartItems?.reduce((totalItemQty, sharedCartItem) => {
    const isUsersItem = sharedCartItem.owner_id === anonymousUserId;
    const isItem = sharedCartItem.menu_item_id === itemId;
    return isUsersItem && isItem ? totalItemQty + (sharedCartItem?.qty ?? 0) : totalItemQty;
  }, 0);
  const regularCartItemQty = regularCartItems.reduce(
    (totalItemQty, item) => (item.menu_item_id === itemId ? totalItemQty + (item?.qty ?? 0) : totalItemQty),
    0
  );
  const quantityInCart = (isSharedCart ? sharedCartItemQty : regularCartItemQty) ?? 0;

  // Create new cart item
  const cartItemParams = {
    menu_id: menuId ?? '',
    menu_item_id: itemId,
    special_instructions: '',
  };
  const newCartItem =
    isSharedCart && sharedCartReference
      ? new SharedCartItem(rootStore, sharedCartReference, {
          owner_id: anonymousUserId,
          ...cartItemParams,
        })
      : new CartItem(menuDataStore, cartItemParams);

  // Check for required modifiers
  const { menuItem } = newCartItem;
  const hasRequiredModifiers = menuItem.modifierGroups?.some(
    (modGroup) => menuDataStore.modifierGroupsById[modGroup.modifierGroupId]?.is_required
  );

  // Get existing cart item
  const existingItems = isSharedCart ? sharedCartItems : regularCartItems;
  const existingCartItem = existingItems?.find((item) => item.menu_item_id === itemId);

  return {
    hasRequiredModifiers,
    existingCartItem,
    quantityInCart,
    newCartItem,
  };
};
